import type { MapConfig, MapStyles, CategoryColorMap, FloorOption } from "../types";

export const MAP_CONFIG: MapConfig = {
  minX: 0, // 元座標系のX軸最小値
  minY: 0, // 元座標系のY軸最小値
  maxX: 1162, // 元座標系のX軸最大値
  maxY: 1167, // 元座標系のY軸最大値
  targetWidth: 0.01162, // 表示時の目標幅
  hallwayY: 0.00075, // 廊下用のY軸オフセット
  toleranceThreshold: 0.0001, // 座標計算の許容誤差
  minZoom: 15.6, // 最小ズームレベル
  maxZoom: 20, // 最大ズームレベル
  initialZoom: 17.5, // 初期ズームレベル
  birdseyePitch: 50, // 初期鳥瞰ピッチ角度
  mobileMinZoom: 15, // モバイル用最小ズームレベル
  mobileMaxZoom: 19, // モバイル用最大ズームレベル
  mobileBreakpoint: 640, // モバイル判定ブレークポイント（px）
  iconMinZoom: 16.5, // アイコンを表示する最小ズームレベル
  panPadding: 0.001, // パン制限の最小余白
  panPaddingRatio: 1, // マップサイズに対する余白の割合
} as const;

// ラベル表示関連の共通設定
export const DEFAULT_ROOM_LABEL_TEXT_SIZE = 17; // デフォルトの部屋ラベルテキストサイズ
export const LOW_PRIORITY_LABEL_MIN_ZOOM = 18; // 低優先度ラベルの最小ズームレベル (minZoomより大きい値とする)

// 診断レイヤの設定値は src/lib/diagnostics/config.ts に集約 (sd3)。

/**
 * マップ表示のスタイル設定
 */
export const MAP_STYLES: MapStyles = {
  background: "#ffffff", // マップ背景色
  baseColor: "#f8f7f6", // ベースポリゴン（建物の外側）の色
  defaultRoomFill: "#edebe6", // カテゴリ未設定の部屋のデフォルト色
  roomOutlineColor: "#c0c0c0", // 部屋の輪郭線の色
  roomOutlineWidth: 2, // 部屋の輪郭線の幅
  routeStartColor: "#ffababff", // ルート表示の線の色
  routeGoalColor: "#B80001", // グラデーション
  routeWidth: 10, // ルート表示の線の幅
  stairsLineColor: "#a9a9a9", // 階段の線の色
} as const;

/**
 * カテゴリとカラーのマッピング
 */
export const CATEGORY_COLORS: CategoryColorMap = [
  {
    color: "#f5effe",
    categories: ["faculty"],
  },
  {
    color: "#e9f5fc",
    categories: ["facility"],
  },
  {
    color: "#faf0cd",
    categories: ["classroom"],
  },
  {
    color: "#a3ceff",
    categories: ["mens_restroom", "mens_shower"],
  },
  {
    color: "#ffa196",
    categories: ["womens_restroom", "womens_shower"],
  },
  {
    color: "#b9dc87",
    categories: ["barrier_free_restroom", "shower"],
  },
  {
    color: "#faf0e1",
    categories: ["elevator", "stairs"],
  },
  {
    color: MAP_STYLES.stairsLineColor,
    categories: ["stairs-arrow-head"],
  },
  {
    color: "#e6e6eb",
    categories: ["other", "entrance", "studio", "lounge", "mall"],
  },
] as const;

/** 施設カード左端の色。地図の塗り色と同じ色相で、白背景で見える程度に濃くしたもの */
export const CATEGORY_ACCENT_COLORS: CategoryColorMap = [
  { color: "#d8c4f7", categories: ["faculty"] },
  { color: "#a0d3f1", categories: ["facility"] },
  { color: "#ecd277", categories: ["classroom"] },
];
export const DEFAULT_CATEGORY_ACCENT_COLOR = "#ccccd6";

// ラベルを表示しないID
export const LABEL_EXCLUDED_IDS = ["void", "semicircle"] as const;

// ラベルを表示しない部屋名
export const LABEL_EXCLUDED_ROOMS = ["base"] as const;

// ラベルを表示しないカテゴリ
export const LABEL_EXCLUDED_CATEGORIES = [
  "elevator",
  "mens_restroom",
  "womens_restroom",
  "barrier_free_restroom",
  "stairs",
] as const;

//アイコン画像読み込み時の名前、ポリゴンデータからアイコンデータを作成するときのフィルタ用カテゴリとして使われる
export const ICON_NAMES: string[] = [
  "elevator",
  "barrier_free_restroom",
  "mens_restroom",
  "womens_restroom",
  // 屋外入口
  "outdoor_entrance",
  "vending",
  "emergency_exit",
  "AED",
];

export const FLOOR_OPTIONS: FloorOption[] = [
  { id: "main-1", label: "本部棟 1階", path: "/main-1.geojson" },
  { id: "main-2", label: "本部棟 2階", path: "/main-2.geojson" },
  { id: "main-3", label: "本部棟 3階", path: "/main-3.geojson" },
  { id: "main-4", label: "本部棟 4階", path: "/main-4.geojson" },
  { id: "main-5", label: "本部棟 5階", path: "/main-5.geojson" },
  { id: "research-1", label: "研究棟 1階", path: "/research-1.geojson" },
  { id: "research-2", label: "研究棟 2階", path: "/research-2.geojson" },
];

export const DIGITAL_SIGNAGE_LOCATION = {
  position: {
    x: 0.00752539179870837,
    y: 0.0015933456763264076,
  },
  floorId: "main-3",
};

/**
 * 建物の構成情報（階層セレクタ用）
 */
export const BUILDINGS = [
  { id: "main", label: "本部棟", floors: [1, 2, 3, 4, 5] },
  { id: "research", label: "研究棟", floors: [1, 2] },
] as const;

/**
 * 屋外入口の矢印の向き定義
 * key: `${x}:${y}` (GeoJSONの座標)
 * value: 角度
 */
export const OUTDOOR_ENTRANCE_ROTATIONS: Record<string, number> = {
  "0.5:179": 0, // 本部棟1階 左側
  "752.5:0": 270, // 本部棟3階 正面
  "1046.5:214": 180, // 本部棟3階 夜間休日
};
